import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { AuthService } from '../services/authService';
import { getThemeAssets } from '../utils/theme';

const CrmProvision: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'setup_required' | 'creating' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [countdown, setCountdown] = useState<number>(5);
  const leftSideRef = useRef<HTMLDivElement>(null);

  // Organization form data
  const [formData, setFormData] = useState({
    org_name: '',
    industry: '',
    website_url: '',
    address: '',
  });
  const [formError, setFormError] = useState<string>('');

  const industries = [
    'Aerospace',
    'Advertising Agency',
    'Ecommerce',
    'Education',
    'Entertainment',
    'Fashion',
    'Finance',
    'FMCG',
    'Healthcare',
    'Hospitality',
    'Information Technology',
    'Manufacturing',
    'Oil & Gas',
    'Real Estate/Construction',
    'Retail',
    'Telecom',
    'Other',
  ];

  // Get theme assets from environment
  const themeAssets = getThemeAssets();

  // Mouse tracking for interactive effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (leftSideRef.current) {
        const rect = leftSideRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height
        });
      }
    };

    const leftSide = leftSideRef.current;
    if (leftSide) {
      leftSide.addEventListener('mousemove', handleMouseMove);
      return () => leftSide.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  // Countdown timer for success state
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/leads');
    }
  }, [status, countdown, navigate]);

  useEffect(() => {
    checkProvisionStatus();
  }, []);

  const checkProvisionStatus = async () => {
    try {
      // Get user data from cookies
      const userInfoCookie = Cookies.get('user_info');

      if (!userInfoCookie) {
        setStatus('error');
        setErrorMessage('User information not found. Please login again.');
        return;
      }

      // Parse user info
      let userInfo;
      try {
        userInfo = JSON.parse(userInfoCookie);
      } catch (parseError) {
        console.error('Failed to parse user info:', parseError);
        setStatus('error');
        setErrorMessage('Invalid user data. Please login again.');
        return;
      }

      // Extract data for provision endpoint
      const provisionData = {
        email: userInfo.email,
        name: userInfo.name,
        phone: userInfo.phone || undefined,
        org_id: userInfo.organizations?.[0]?.org_id || undefined
      };

      // Validate required fields
      if (!provisionData.email || !provisionData.name) {
        setStatus('error');
        setErrorMessage('Missing required user information.');
        return;
      }

      // Call provision endpoint
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/crm/provision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(provisionData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.meta?.message || 'Provisioning failed');
      }

      // Check if provisioning was successful
      if (data.meta?.status === 200 || data.meta?.status === 201) {
        // Setup complete - user already has CRM
        const crmToken = data.data?.crm_token;
        const userInfo = data.data?.user_info;

        if (!crmToken) {
          throw new Error('CRM token not received from server');
        }

        if (!userInfo) {
          throw new Error('User info not received from server');
        }

        // Store CRM token as crm_auth_token cookie (same as login)
        AuthService.setToken(crmToken, { expires: 7 });

        // Store user data in localStorage (same as login)
        AuthService.setUserData(userInfo);
        localStorage.setItem('crm_user', JSON.stringify(userInfo));

        console.log('CRM provision successful, user data stored:', userInfo);

        // Set success status - will trigger 5-second countdown
        setStatus('success');
      } else if (data.meta?.status === 202) {
        // Setup incomplete - show organization form
        setStatus('setup_required');
      } else {
        throw new Error('Unexpected response from server');
      }

    } catch (error: any) {
      console.error('Provision error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Failed to check CRM status. Please try again.');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!formData.org_name.trim()) {
      setFormError('Organization name is required');
      return;
    }
    if (!formData.industry) {
      setFormError('Industry is required');
      return;
    }

    setFormError('');
    setStatus('creating');

    try {
      // Get user data from cookies
      const userInfoCookie = Cookies.get('user_info');
      if (!userInfoCookie) {
        throw new Error('User information not found');
      }

      const userInfo = JSON.parse(userInfoCookie);

      // Call provision endpoint with organization details
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/crm/provision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userInfo.email,
          name: userInfo.name,
          phone: userInfo.phone || undefined,
          org_id: userInfo.organizations?.[0]?.org_id || undefined,
          org_name: formData.org_name,
          industry: formData.industry,
          website_url: formData.website_url || undefined,
          address: formData.address || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.meta?.message || 'Failed to create CRM account');
      }

      if (data.meta?.status === 201) {
        // CRM created successfully
        const crmToken = data.data?.crm_token;
        const userInfo = data.data?.user_info;

        if (!crmToken || !userInfo) {
          throw new Error('Incomplete response from server');
        }

        // Store authentication data
        AuthService.setToken(crmToken, { expires: 7 });
        AuthService.setUserData(userInfo);
        localStorage.setItem('crm_user', JSON.stringify(userInfo));

        console.log('CRM account created successfully:', userInfo);

        // Set success status - will trigger 5-second countdown
        setStatus('success');
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      setStatus('setup_required');
      setFormError(error.message || 'Failed to create CRM account. Please try again.');
    }
  };

  return (
    <div className="h-screen w-screen flex m-0 p-0">
      {/* Left Side - Glass Morphism Cards with Background */}
      <div
        ref={leftSideRef}
        className="w-1/2 h-full relative overflow-hidden"
      >
        {/* Background overlay */}
        <div className="absolute inset-0 bg-login-bgLeft"></div>

        {/* Animated Rocket Images */}
        <div
          className="absolute w-24 h-24 transition-transform duration-1000 ease-out opacity-80 hover:opacity-100"
          style={{
            transform: `translate(${mousePosition.x * 60}px, ${mousePosition.y * 40}px) rotate(${mousePosition.x * 10}deg)`,
            top: '10%',
            left: '10%'
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-16 h-16 transition-transform duration-800 ease-out opacity-60 hover:opacity-90"
          style={{
            transform: `translate(${mousePosition.x * -40}px, ${mousePosition.y * 60}px) rotate(${mousePosition.y * 15}deg)`,
            top: '65%',
            right: '15%'
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-14 h-14 transition-transform duration-1200 ease-out opacity-50 hover:opacity-80"
          style={{
            transform: `translate(${mousePosition.x * 30}px, ${mousePosition.y * -35}px) rotate(${mousePosition.x * -8}deg)`,
            top: '40%',
            left: '75%'
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-18 h-18 transition-transform duration-900 ease-out opacity-40 hover:opacity-70"
          style={{
            transform: `translate(${mousePosition.x * -25}px, ${mousePosition.y * 20}px) rotate(${mousePosition.y * -12}deg)`,
            top: '80%',
            left: '30%'
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center px-12 py-16">
          {/* Main Heading */}
          <div className="text-left mb-12 w-full max-w-lg">
            <h1 className="text-5xl font-bold mb-4 text-white leading-tight">
              {status === 'setup_required' ? 'Complete Your Setup' : 'Setting Up Your CRM'}
            </h1>
            <p className="text-xl text-blue-100/90 font-light mb-2">
              {status === 'setup_required' ? 'Tell us about your organization' : 'Creating your personalized workspace'}
            </p>
            <p className="text-lg text-blue-200/80 font-medium">
              {status === 'setup_required'
                ? 'Just a few more details to get started.'
                : 'Your all-in-one CRM solution is being prepared.'}
            </p>
          </div>

          {/* Feature Cards */}
          <div className="space-y-6 w-full max-w-lg">
            {/* Pipeline Management Card */}
            <div
              className="group relative p-6 rounded-2xl backdrop-blur-lg bg-white/10 border border-white/20 shadow-2xl transition-all duration-300 hover:bg-white/15 hover:border-white/30 hover:scale-105 cursor-pointer"
              style={{
                transform: `perspective(1000px) rotateX(${mousePosition.y * 5 - 2.5}deg) rotateY(${mousePosition.x * 5 - 2.5}deg)`
              }}
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-br from-brandColor/20 to-brandColorDark/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-6 h-6 text-white opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-white/90 transition-colors text-left">
                    Pipeline Management
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed text-left">
                    Visualize your sales process with customizable pipeline stages and drag-drop functionality.
                  </p>
                </div>
              </div>
            </div>

            {/* Lead Tracking Card */}
            <div
              className="group relative p-6 rounded-2xl backdrop-blur-lg bg-white/10 border border-white/20 shadow-2xl transition-all duration-300 hover:bg-white/15 hover:border-white/30 hover:scale-105 cursor-pointer"
              style={{
                transform: `perspective(1000px) rotateX(${mousePosition.y * -3}deg) rotateY(${mousePosition.x * -3}deg)`,
                transitionDelay: '50ms'
              }}
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-6 h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-green-100 transition-colors text-left">
                    Lead Tracking
                  </h3>
                  <p className="text-sm text-green-100/80 leading-relaxed text-left">
                    Track every interaction and convert prospects into customers with intelligent lead management.
                  </p>
                </div>
              </div>
            </div>

            {/* Reports & Analytics Card */}
            <div
              className="group relative p-6 rounded-2xl backdrop-blur-lg bg-white/10 border border-white/20 shadow-2xl transition-all duration-300 hover:bg-white/15 hover:border-white/30 hover:scale-105 cursor-pointer"
              style={{
                transform: `perspective(1000px) rotateX(${mousePosition.y * 4}deg) rotateY(${mousePosition.x * 4}deg)`,
                transitionDelay: '100ms'
              }}
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-6 h-6 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-100 transition-colors text-left">
                    Reports & Analytics
                  </h3>
                  <p className="text-sm text-purple-100/80 leading-relaxed text-left">
                    Make data-driven decisions with comprehensive reports and real-time analytics dashboard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Status / Form */}
      <div className="w-1/2 h-full bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center overflow-y-auto">
        <div className="w-full max-w-md mx-8 my-4">
          <div className="text-center">
            <img
              src={themeAssets.loginLogo}
              alt={themeAssets.brandName}
              className="h-12 mx-auto mb-8"
            />

            {status === 'loading' && (
              <>
                {/* Loading State */}
                <div className="mb-8">
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="w-20 h-20 border-4 border-gray-200 border-t-brandColor rounded-full animate-spin" />
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent mb-4">
                    Checking Your Account
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Please wait while we check your CRM status...
                  </p>
                </div>
              </>
            )}

            {status === 'setup_required' && (
              <>
                {/* Organization Form */}
                <div className="mb-6">
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent mb-2">
                    Organization Details
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Complete your CRM setup with organization information
                  </p>
                </div>

                {formError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-left">
                    <p className="text-red-700 text-sm">{formError}</p>
                  </div>
                )}

                <form onSubmit={handleFormSubmit} className="space-y-4 text-left">
                  <div>
                    <label htmlFor="org_name" className="block text-sm font-medium text-gray-700 mb-2">
                      Organization Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="org_name"
                      value={formData.org_name}
                      onChange={(e) => setFormData({ ...formData, org_name: e.target.value })}
                      placeholder="Acme Corporation"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all duration-200"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                      Industry <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="industry"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all duration-200"
                      required
                    >
                      <option value="">Select your industry</option>
                      {industries.map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="website_url" className="block text-sm font-medium text-gray-700 mb-2">
                      Website URL (Optional)
                    </label>
                    <input
                      type="url"
                      id="website_url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      placeholder="https://www.yourcompany.com"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                      Address (Optional)
                    </label>
                    <textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Company address"
                      rows={3}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all duration-200 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-brandColor to-brandColorDark text-white py-3 px-4 rounded-lg font-medium hover:from-brandColorDark hover:to-brandColor focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    Create CRM Account
                  </button>
                </form>
              </>
            )}

            {status === 'creating' && (
              <>
                {/* Creating State */}
                <div className="mb-8">
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="w-20 h-20 border-4 border-gray-200 border-t-brandColor rounded-full animate-spin" />
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent mb-4">
                    Creating Your CRM
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Please wait while we set up your account...
                  </p>

                  <div className="space-y-3 text-sm text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-2 h-2 bg-brandColor rounded-full animate-pulse" />
                      <span>Creating your workspace</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-2 h-2 bg-brandColor rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                      <span>Configuring industry-specific pipelines</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-2 h-2 bg-brandColor rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                      <span>Setting up your admin account</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                {/* Success State with Countdown */}
                <div className="mb-8">
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent mb-4">
                    CRM Setup Complete!
                  </h2>
                  <p className="text-gray-600 mb-4">
                    Your CRM account is ready to use.
                  </p>
                  <p className="text-lg text-gray-700 font-medium">
                    Redirecting to Leads in <span className="text-brandColor font-bold">{countdown}</span> seconds...
                  </p>
                </div>

                {/* Success Actions */}
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/leads')}
                    className="w-full bg-gradient-to-r from-brandColor to-brandColorDark text-white py-3 px-4 rounded-lg font-medium hover:from-brandColorDark hover:to-brandColor focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    Go to Leads Now
                  </button>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                {/* Error State */}
                <div className="mb-8">
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent mb-4">
                    Setup Failed
                  </h2>
                  <p className="text-gray-600 mb-2">
                    {errorMessage}
                  </p>
                </div>

                {/* Error Actions */}
                <div className="space-y-3">
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full bg-gradient-to-r from-brandColor to-brandColorDark text-white py-3 px-4 rounded-lg font-medium hover:from-brandColorDark hover:to-brandColor focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full bg-white text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transform hover:scale-105 transition-all duration-200 shadow border border-gray-300"
                  >
                    Back to Login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmProvision;
